import { motion } from "framer-motion";
import { Rocket, Zap, Gamepad2 } from "lucide-react";
import { Link } from "react-router-dom";
import CopyCA from "./CopyCA";
import StakeSoonButton from "./StakeSoon";

const HERO_BG = "/game/gg-hero-bg.jpg";

export const Hero = () => {
  return (
    <section
      id="top"
      className="relative overflow-hidden"
      style={{
        backgroundImage: `linear-gradient(180deg, rgba(5,5,10,0.72) 0%, rgba(5,5,10,0.55) 45%, rgba(5,5,10,0.94) 100%), url(${HERO_BG})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
      data-testid="hero-section"
    >
      <div className="absolute inset-0 gg-scanlines pointer-events-none" />
      {/* radial neon bleed */}
      <div
        className="absolute -top-32 -left-32 w-[560px] h-[560px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(0,229,255,0.16), transparent 65%)" }}
        aria-hidden="true"
      />
      <div
        className="absolute -bottom-40 -right-24 w-[520px] h-[520px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(255,0,127,0.14), transparent 65%)" }}
        aria-hidden="true"
      />
      {/* rising neon sparks */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true" data-testid="hero-bubbles">
        {[
          { l: "6%", s: 8, d: 9, delay: 0 },
          { l: "14%", s: 12, d: 12, delay: 2.5 },
          { l: "27%", s: 6, d: 8, delay: 4.2 },
          { l: "38%", s: 10, d: 11, delay: 1.3 },
          { l: "52%", s: 7, d: 9.5, delay: 5.5 },
          { l: "63%", s: 13, d: 13, delay: 0.8 },
          { l: "74%", s: 8, d: 10, delay: 3.6 },
          { l: "85%", s: 11, d: 12.5, delay: 6.2 },
          { l: "93%", s: 6, d: 8.5, delay: 2 },
        ].map((b, i) => (
          <span
            key={i}
            className="bubble"
            style={{ left: b.l, width: b.s, height: b.s, animationDuration: `${b.d}s`, animationDelay: `${b.delay}s` }}
          />
        ))}
      </div>

      <div className="relative mx-auto max-w-6xl px-4 md:px-8 pt-16 pb-28 md:pt-24 md:pb-36 grid md:grid-cols-2 gap-10 items-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="min-w-0"
        >
          <span
            className="inline-flex items-center gap-2 gg-terminal rounded-full px-4 py-1.5 font-accent text-base text-[var(--jd-primary)]"
            data-testid="hero-badge"
          >
            <Zap size={14} /> LIVE ON SOLANA
          </span>

          <h1
            className="font-display font-900 text-4xl sm:text-5xl lg:text-6xl leading-[1.02] mt-6 uppercase neon-title"
            style={{ fontWeight: 900 }}
            data-testid="hero-title"
          >
            Ride the Wave
            <br />
            with{" "}
            <span className="neon-title-gold" style={{ color: "var(--jd-secondary)" }}>
              JetDog
            </span>
          </h1>

          <p className="mt-5 text-base md:text-lg max-w-lg text-[var(--jd-ocean)]" data-testid="hero-subtitle">
            The good boy on a jetski, tearing through the neon Solana ocean.
            Play the runner, stack your rewards, claim your bag on-chain.
          </p>

          <div className="mt-8 flex flex-wrap gap-4" data-testid="hero-cta-row">
            <a
              href="https://pump.fun/8Bdum5DBYXqqi5BeGej45vKVifELQM7pASUfysLTpump"
              target="_blank"
              rel="noreferrer"
              className="nb-btn"
              data-testid="hero-buy-button"
            >
              <Rocket size={18} /> Buy on Pump.fun
            </a>
            <Link to="/play" className="nb-btn nb-btn--cyan" data-testid="hero-play-button">
              <Gamepad2 size={18} /> Play & Earn
            </Link>
            <StakeSoonButton className="nb-btn nb-btn--accent" testid="hero-stake-button" />
          </div>

          <div className="mt-8 max-w-md" data-testid="hero-ca-row">
            <p className="font-accent text-xl text-[var(--jd-primary)]">
              &gt;_ Contract Address (Solana)
            </p>
            <div className="mt-2">
              <CopyCA size="lg" />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="relative hidden md:block min-h-[22rem]"
          aria-hidden="true"
        />
      </div>
    </section>
  );
};

export default Hero;
